import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import CallMadeRounded from '@mui/icons-material/CallMadeRounded';
import CallReceivedRounded from '@mui/icons-material/CallReceivedRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import { Box, Link, Stack, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { api, unwrapJson } from '../../../api/client';
import { EmptyState } from '../../../components/states';
import { humanize } from '../../../components/StatusChip';
import { businessDate, moneyCompact } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { Donut, PeriodBars, ShareBar } from '../components/charts';
import { ReportFrame } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { linkTo } from '../components/useEntityLink';
import { Panel, QueryError, StatGrid, StatTile } from '../components/ui';
import { useReportParams, usePeriodText } from '../components/useReportParams';
import { fetchCsv, reportFileName } from '../lib/csv';
import { AGING_BUCKETS, AGING_COLORS, overdueShare } from '../lib/aging';
import { num } from '../lib/numbers';
import { ChartRow, ItemCell, M, RangeControls } from './common';

function AgingReport({ ledger }: { ledger: 'RECEIVABLE' | 'PAYABLE' }) {
  const p = useReportParams();
  const { format } = useShop();
  const slug = ledger === 'RECEIVABLE' ? 'receivables-aging' : 'payables-aging';
  const party = ledger === 'RECEIVABLE' ? 'customer' : 'supplier';
  const query = { ledger, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['reports', 'aging', query],
    queryFn: () => unwrapJson(api.GET('/reports/aging', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const t = q.data?.totals;
  const share = t ? overdueShare(t) : 0;
  const bucketCol = (
    key: (typeof AGING_BUCKETS)[number]['key'],
    label: string,
    hide?: 'sm' | 'md' | 'lg',
  ) => ({
    key,
    header: label,
    align: 'right' as const,
    hideBelow: hide,
    render: (r: NonNullable<typeof q.data>['data'][number]) =>
      num(r[key]) === 0 ? (
        <Box component="span" sx={{ color: 'text.disabled' }}>
          —
        </Box>
      ) : (
        <Box component="span" sx={{ color: key === 'over90' ? 'error.main' : undefined }}>
          <M v={r[key]} />
        </Box>
      ),
    footer: <M v={t?.[key]} />,
  });
  return (
    <ReportFrame
      slug={slug}
      period={q.data ? `As of ${businessDate(q.data.asOf, format)}` : undefined}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/aging', {
            params: { query: { ledger, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName(slug, q.data?.asOf ?? p.today),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label={ledger === 'RECEIVABLE' ? 'Customers owe you' : 'You owe suppliers'}
          value={t && moneyCompact(t.net, format)}
          hint={
            t &&
            `Outstanding ${moneyCompact(t.outstanding, format)} · credits ${moneyCompact(t.credit, format)}`
          }
          icon={ledger === 'RECEIVABLE' ? <CallReceivedRounded /> : <CallMadeRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Overdue"
          value={t && moneyCompact(num(t.outstanding) - num(t.current), format)}
          hint={`${(share * 100).toFixed(0)}% of the outstanding amount`}
          icon={<HourglassBottomRounded />}
          tone={share > 0.25 ? 'error' : 'warning'}
          loading={q.isLoading}
        />
        <StatTile
          label="Over 90 days"
          value={t && moneyCompact(t.over90, format)}
          icon={<ReportProblemOutlined />}
          tone="error"
          loading={q.isLoading}
        />
        <StatTile
          label={ledger === 'RECEIVABLE' ? 'Customers' : 'Suppliers'}
          value={q.data?.page.total}
          hint="With an open balance"
          icon={<PeopleAltOutlined />}
          tone="info"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Aging profile" subtitle="Outstanding amounts by days past due" sx={{ mb: 2.5 }}>
        {t ? (
          <ShareBar
            parts={AGING_BUCKETS.map((b) => ({
              label: b.label,
              value: num(t[b.key]),
              color: AGING_COLORS[b.key],
            }))}
          />
        ) : (
          <Box sx={{ height: 64 }} />
        )}
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(r) => r.contactId}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        dense
        empty={
          <EmptyState
            icon={<PaymentsOutlined />}
            title={ledger === 'RECEIVABLE' ? 'Nobody owes you anything' : 'You owe nothing'}
            body={`No ${party} has an open balance right now.`}
          />
        }
        columns={[
          {
            key: 'name',
            header: ledger === 'RECEIVABLE' ? 'Customer' : 'Supplier',
            render: (r) => {
              const to = linkTo(party, r.contactId);
              return (
                <Box sx={{ minWidth: 0 }}>
                  {to ? (
                    <Link component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" component="div">
                    {[
                      r.phone,
                      r.oldestDueDate && `oldest due ${businessDate(r.oldestDueDate, format)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              );
            },
          },
          bucketCol('current', 'Not due', 'md'),
          bucketCol('days1To30', '1–30', 'md'),
          bucketCol('days31To60', '31–60', 'lg'),
          bucketCol('days61To90', '61–90', 'lg'),
          bucketCol('over90', '90+', 'sm'),
          {
            key: 'credit',
            header: 'Credits',
            align: 'right',
            hideBelow: 'lg',
            render: (r) => (num(r.credit) ? <M v={-num(r.credit)} signed /> : '—'),
            footer: num(t?.credit) ? <M v={-num(t?.credit)} signed /> : '—',
          },
          {
            key: 'net',
            header: 'Balance',
            align: 'right',
            render: (r) => <M v={r.net} strong />,
            footer: <M v={t?.net} />,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function ReceivablesAgingReport() {
  return <AgingReport ledger="RECEIVABLE" />;
}

export function PayablesAgingReport() {
  return <AgingReport ledger="PAYABLE" />;
}

const PURPOSE_LABEL: Record<string, string> = {
  RECEIPT: 'Customer receipts',
  SUPPLIER_PAYMENT: 'Supplier payments',
  CUSTOMER_REFUND: 'Customer refunds',
  SUPPLIER_REFUND: 'Supplier refunds',
};

export function PaymentsReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'payments', p.range],
    queryFn: () => unwrapJson(api.GET('/reports/payments', { params: { query: p.range } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const t = q.data?.totals;
  const byMethod = (direction: 'IN' | 'OUT') => {
    const m = new Map<string, { id: string; label: string; value: number }>();
    for (const r of rows ?? [])
      if (r.direction === direction) {
        const cur = m.get(r.methodId) ?? { id: r.methodId, label: r.method, value: 0 };
        cur.value += num(r.amount);
        m.set(r.methodId, cur);
      }
    return [...m.values()];
  };
  return (
    <ReportFrame
      slug="payments"
      period={usePeriodText(p)}
      controls={<RangeControls p={p} />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/payments', {
            params: { query: { ...p.range, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('payments', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Money in"
          value={t && moneyCompact(t.moneyIn, format)}
          icon={<CallReceivedRounded />}
          tone="success"
          loading={q.isLoading}
        />
        <StatTile
          label="Money out"
          value={t && moneyCompact(t.moneyOut, format)}
          icon={<CallMadeRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Net"
          value={t && moneyCompact(t.net, format)}
          hint={t && `${String(t.count)} payments`}
          icon={<SwapVertRounded />}
          tone={num(t?.net) < 0 ? 'error' : 'primary'}
          loading={q.isLoading}
        />
      </StatGrid>
      <ChartRow ratio="1fr 1fr">
        <Panel title="Money in by method">
          <Donut loading={q.isLoading} centerLabel="In" items={byMethod('IN')} />
        </Panel>
        <Panel title="Money out by method">
          <Donut loading={q.isLoading} centerLabel="Out" items={byMethod('OUT')} />
        </Panel>
      </ChartRow>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => `${r.methodId}:${r.purpose}`}
        empty={
          <EmptyState
            icon={<PaymentsOutlined />}
            title="No payments in this period"
            body="Receipts, supplier payments and refunds appear here once posted."
          />
        }
        columns={[
          { key: 'method', header: 'Method', render: (r) => <ItemCell name={r.method} /> },
          {
            key: 'purpose',
            header: 'Purpose',
            hideBelow: 'sm',
            render: (r) => PURPOSE_LABEL[r.purpose] ?? humanize(r.purpose),
          },
          {
            key: 'count',
            header: 'Payments',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => r.count,
            footer: t?.count,
          },
          {
            key: 'in',
            header: 'In',
            align: 'right',
            render: (r) => (r.direction === 'IN' ? <M v={r.amount} strong /> : ''),
            footer: <M v={t?.moneyIn} />,
          },
          {
            key: 'out',
            header: 'Out',
            align: 'right',
            render: (r) => (r.direction === 'OUT' ? <M v={r.amount} strong /> : ''),
            footer: <M v={t?.moneyOut} />,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function CashMovementReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'cash-movement', p.range],
    queryFn: () => unwrapJson(api.GET('/reports/cash-movement', { params: { query: p.range } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const t = q.data?.totals;
  return (
    <ReportFrame
      slug="cash-movement"
      period={usePeriodText(p)}
      controls={<RangeControls p={p} />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/cash-movement', {
            params: { query: { ...p.range, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('cash-movement', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Opening"
          value={t && moneyCompact(t.opening, format)}
          icon={<AccountBalanceOutlined />}
          tone="neutral"
          loading={q.isLoading}
        />
        <StatTile
          label="Inflows"
          value={t && moneyCompact(t.inflows, format)}
          icon={<CallReceivedRounded />}
          tone="success"
          loading={q.isLoading}
        />
        <StatTile
          label="Outflows"
          value={t && moneyCompact(t.outflows, format)}
          icon={<CallMadeRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Closing"
          value={t && moneyCompact(t.closing, format)}
          hint={t && `Change ${moneyCompact(num(t.closing) - num(t.opening), format)}`}
          icon={<AccountBalanceOutlined />}
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Inflows and outflows" subtitle="Per cash and bank account" sx={{ mb: 2.5 }}>
        <PeriodBars
          loading={q.isLoading}
          labels={(rows ?? []).map((r) => r.name)}
          series={[
            {
              key: 'in',
              label: 'Inflows',
              data: (rows ?? []).map((r) => num(r.inflows)),
              color: 'var(--mui-palette-success-main)',
            },
            {
              key: 'out',
              label: 'Outflows',
              data: (rows ?? []).map((r) => num(r.outflows)),
              color: 'var(--mui-palette-warning-main)',
            },
          ]}
          height={260}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => r.accountId}
        empty={
          <EmptyState
            icon={<AccountBalanceOutlined />}
            title="No cash or bank accounts"
            body="Payment accounts appear here once they are set up."
          />
        }
        columns={[
          {
            key: 'name',
            header: 'Account',
            render: (r) => {
              const to = linkTo('account', r.accountId);
              return (
                <Stack>
                  {to ? (
                    <Link component={RouterLink} to={to} underline="hover" sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {r.code}
                  </Typography>
                </Stack>
              );
            },
          },
          {
            key: 'opening',
            header: 'Opening',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <M v={r.opening} />,
            footer: <M v={t?.opening} />,
          },
          {
            key: 'in',
            header: 'Inflows',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.inflows} />,
            footer: <M v={t?.inflows} />,
          },
          {
            key: 'out',
            header: 'Outflows',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.outflows} />,
            footer: <M v={t?.outflows} />,
          },
          {
            key: 'closing',
            header: 'Closing',
            align: 'right',
            render: (r) => <M v={r.closing} strong signed />,
            footer: <M v={t?.closing} signed />,
          },
        ]}
      />
    </ReportFrame>
  );
}
